import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import type { Editor } from "@tiptap/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

// --- Hooks ---
import { useTiptapEditor } from "app/components/TextEditor/hooks/use-tiptap-editor";

// --- UI Primitives ---
import {
  Button,
  ButtonGroup,
} from "app/components/TextEditor/components/tiptap-ui-primitive/button";
import {
  Card,
  CardBody,
  CardItemGroup,
} from "app/components/TextEditor/components/tiptap-ui-primitive/card";
import { Separator } from "app/components/TextEditor/components/tiptap-ui-primitive/separator";

// --- Icons ---
import { TrashIcon } from "app/components/TextEditor/components/tiptap-icons/trash-icon";

// --- Styles ---
import { CopyIcon } from "app/components/TextEditor/components/tiptap-icons/copy-icon";
import { HeaderColumnIcon } from "app/components/TextEditor/components/tiptap-icons/header-column-icon";
import { HeaderRowIcon } from "app/components/TextEditor/components/tiptap-icons/header-row-icon";
import { InsertColumnIcon } from "app/components/TextEditor/components/tiptap-icons/insert-column-icon";
import { InsertRowIcon } from "app/components/TextEditor/components/tiptap-icons/insert-row-icon";
import { MergeCellIcon } from "app/components/TextEditor/components/tiptap-icons/merge-cell-icon";
import { SplitCellIcon } from "app/components/TextEditor/components/tiptap-icons/split-cell-icon";
import "app/components/TextEditor/components/tiptap-ui/table-floating-menu/table-floating-menu.scss";

export interface TableFloatingMenuProps {
  editor?: Editor | null;
  isEditable?: boolean;
}

export function TableFloatingMenu({
  editor: providedEditor,
  isEditable = false,
}: TableFloatingMenuProps) {
  // States
  const [isVisible, setIsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showHeaderOperations, setShowHeaderOperations] = useState(false);

  const referenceCellRef = useRef<HTMLElement | null>(null);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const referenceCellRect = referenceCellRef.current?.getBoundingClientRect();

  const { refs: triggerRefs } = useFloating({
    placement: "top",
    middleware: [offset(0), shift({ padding: 8 })],
    whileElementsMounted: (...args) =>
      autoUpdate(...args, {
        elementResize: false,
      }),
  });

  const { refs: menuRefs, floatingStyles: menuStyles } = useFloating({
    placement: "bottom-start",
    middleware: [offset(5), flip(), shift()],
    whileElementsMounted: (...args) =>
      autoUpdate(...args, {
        // Avoid ResizeObserver feedback loops while typing in auto-resizing cells.
        elementResize: false,
      }),
  });

  const { editor: activeEditor } = useTiptapEditor(providedEditor);

  const isFirstRowAndFirstColumn = useCallback((element: HTMLElement) => {
    const tableCell = element.closest("td, th") as HTMLTableCellElement | null;
    const tableRow = tableCell?.closest("tr") as HTMLTableRowElement | null;

    if (!tableCell || !tableRow) {
      return false;
    }

    return tableRow.rowIndex === 0 && tableCell.cellIndex === 0;
  }, []);

  const duplicateRow = useCallback(() => {
    if (!activeEditor) return false;

    const { state, view } = activeEditor;
    const { $from } = state.selection;

    let rowDepth = -1;

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "tableRow" || node.type.spec.tableRole === "row") {
        rowDepth = depth;
        break;
      }
    }

    if (rowDepth < 0) {
      return false;
    }

    const rowNode = $from.node(rowDepth);
    const insertPos = $from.after(rowDepth);
    const duplicatedRow = rowNode.type.create(
      rowNode.attrs,
      rowNode.content,
      rowNode.marks,
    );

    const tr = state.tr.insert(insertPos, duplicatedRow).scrollIntoView();
    view.dispatch(tr);
    return true;
  }, [activeEditor]);

  const duplicateColumn = useCallback(() => {
    if (!activeEditor) return false;

    const { state, view } = activeEditor;
    const { $from } = state.selection;

    let cellDepth = -1;
    let rowDepth = -1;
    let tableDepth = -1;

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      const role = node.type.spec.tableRole;

      if (
        cellDepth < 0 &&
        (node.type.name === "tableCell" || node.type.name === "tableHeader")
      ) {
        cellDepth = depth;
      }

      if (rowDepth < 0 && (node.type.name === "tableRow" || role === "row")) {
        rowDepth = depth;
      }

      if (tableDepth < 0 && (node.type.name === "table" || role === "table")) {
        tableDepth = depth;
      }
    }

    if (cellDepth < 0 || rowDepth < 0 || tableDepth < 0) {
      return false;
    }

    const currentRowNode = $from.node(rowDepth);
    const tableNode = $from.node(tableDepth);
    const tableStart = $from.start(tableDepth);
    const selectedCellOffset = $from.before(cellDepth) - $from.start(rowDepth);

    let targetColumnIndex = -1;
    let scannedColumns = 0;

    currentRowNode.forEach((cell: any, offset: number) => {
      const colspan = Number(cell.attrs?.colspan ?? 1);
      if (offset === selectedCellOffset) {
        targetColumnIndex = scannedColumns;
      }
      scannedColumns += colspan;
    });

    if (targetColumnIndex < 0) {
      return false;
    }

    const insertOperations: Array<{ pos: number; node: any }> = [];

    tableNode.forEach((row: any, rowOffset: number) => {
      let columnCursor = 0;
      let matchOffset = -1;
      let matchCell: any = null;

      row.forEach((cell: any, cellOffset: number) => {
        const colspan = Number(cell.attrs?.colspan ?? 1);
        if (
          matchCell == null &&
          targetColumnIndex >= columnCursor &&
          targetColumnIndex < columnCursor + colspan
        ) {
          matchCell = cell;
          matchOffset = cellOffset;
        }
        columnCursor += colspan;
      });

      if (!matchCell || matchOffset < 0) {
        return;
      }

      const duplicatedCell = matchCell.type.create(
        matchCell.attrs,
        matchCell.content,
        matchCell.marks,
      );

      insertOperations.push({
        pos: tableStart + rowOffset + matchOffset + matchCell.nodeSize,
        node: duplicatedCell,
      });
    });

    if (insertOperations.length === 0) {
      return false;
    }

    const tr = state.tr;
    insertOperations
      .sort((a, b) => b.pos - a.pos)
      .forEach((operation) => tr.insert(operation.pos, operation.node));

    view.dispatch(tr.scrollIntoView());
    return true;
  }, [activeEditor]);

  // Effects
  // Detect focused table cell from editor state
  useEffect(() => {
    if (!activeEditor) return;

    const checkTableCellFocus = () => {
      try {
        const { selection } = activeEditor.state;
        const { $from } = selection;

        // Check all parent nodes to find a table cell
        let cellDepth = -1;

        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (
            node.type.name === "tableCell" ||
            node.type.name === "tableHeader"
          ) {
            cellDepth = depth;
            break;
          }
        }

        if (cellDepth >= 0) {
          // Get the position before this cell node
          const cellPos = $from.before(cellDepth);

          // Find the DOM node at this position
          const domNode = activeEditor.view.nodeDOM(cellPos);

          if (domNode && domNode instanceof HTMLElement) {
            if (referenceCellRef.current !== domNode) {
              referenceCellRef.current = domNode;
              triggerRefs.setReference(domNode);
            }
            setShowHeaderOperations(isFirstRowAndFirstColumn(domNode));
            setIsVisible(true);
            return;
          }

          // Fallback: try domAtPos
          const domAtPos = activeEditor.view.domAtPos(cellPos);
          if (domAtPos?.node) {
            let element = domAtPos.node as HTMLElement;

            // If it's a text node, get the parent element
            if (element.nodeType === Node.TEXT_NODE) {
              element = element.parentElement as HTMLElement;
            }

            // Find the closest td or th
            const cellElement = element?.closest("td, th") as HTMLElement;
            if (cellElement) {
              if (referenceCellRef.current !== cellElement) {
                referenceCellRef.current = cellElement;
                triggerRefs.setReference(cellElement);
              }
              setShowHeaderOperations(isFirstRowAndFirstColumn(cellElement));
              setIsVisible(true);
              return;
            }
          }
        }

        // Not in a table cell
        referenceCellRef.current = null;
        triggerRefs.setReference(null);
        setShowHeaderOperations(false);
        setIsMenuOpen(false);
        setIsVisible(false);
      } catch (error) {
        console.error("Error checking table cell focus:", error);
        referenceCellRef.current = null;
        triggerRefs.setReference(null);
        setShowHeaderOperations(false);
        setIsMenuOpen(false);
        setIsVisible(false);
      }
    };

    // Listen for selection updates
    activeEditor.on("selectionUpdate", checkTableCellFocus);
    activeEditor.on("update", checkTableCellFocus);

    // Initial check
    checkTableCellFocus();

    return () => {
      activeEditor.off("selectionUpdate", checkTableCellFocus);
      activeEditor.off("update", checkTableCellFocus);
    };
  }, [activeEditor, isFirstRowAndFirstColumn, triggerRefs]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = triggerButtonRef.current?.contains(target);
      const clickedMenu = menuContainerRef.current?.contains(target);

      if (!clickedTrigger && !clickedMenu) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  const executeCommand = useCallback(
    (command: () => boolean) => {
      if (command()) {
        activeEditor?.view.focus();
      }
    },
    [activeEditor],
  );

  if (!activeEditor || !isVisible) {
    return null;
  }

  return (
    <FloatingPortal>
      {isEditable ? null : (
        <button
          ref={(node) => {
            triggerButtonRef.current = node;
            triggerRefs.setFloating(node);
          }}
          type="button"
          style={{
            position: "fixed",
            width: referenceCellRect?.width
              ? `${referenceCellRect.width}px`
              : undefined,
            left: referenceCellRect
              ? `${referenceCellRect?.left}px`
              : undefined,
            top: referenceCellRect ? `${referenceCellRect?.top}px` : undefined,
          }}
          className="z-20 relative"
          onMouseDown={(event) => event.preventDefault()}
          aria-label="Open table actions"
          aria-expanded={isMenuOpen}
        >
          <span
            className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            ref={(node) => {
              menuRefs.setReference(node);
            }}
          />
        </button>
      )}

      {isMenuOpen && (
        <div
          ref={(node) => {
            menuContainerRef.current = node;
            menuRefs.setFloating(node);
          }}
          style={menuStyles}
          className="table-floating-menu-content"
        >
          <Card className="table-floating-menu-card max-h-60">
            <CardBody className="table-floating-menu-body">
              {showHeaderOperations && (
                <>
                  {/* Header Operations */}
                  <CardItemGroup>
                    <div className="table-menu-section-title !text-primary">
                      Headers
                    </div>
                    <ButtonGroup orientation="vertical" className="w-full">
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() =>
                          executeCommand(() =>
                            activeEditor
                              .chain()
                              .focus()
                              .toggleHeaderColumn()
                              .run(),
                          )
                        }
                        className="!justify-start"
                      >
                        <HeaderColumnIcon className="w-4 h-4 mr-1" />
                        Header column
                      </Button>
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={() =>
                          executeCommand(() =>
                            activeEditor
                              .chain()
                              .focus()
                              .toggleHeaderRow()
                              .run(),
                          )
                        }
                        className="!justify-start"
                      >
                        <HeaderRowIcon className="w-4 h-4 mr-1" />
                        Header row
                      </Button>
                    </ButtonGroup>
                  </CardItemGroup>

                  <Separator className="my-2" orientation="horizontal" />
                </>
              )}

              {/* Column Operations */}
              <CardItemGroup>
                <div className="table-menu-section-title !text-primary">
                  Columns
                </div>
                <ButtonGroup orientation="vertical" className="w-full">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().addColumnBefore().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <InsertColumnIcon className="w-4 h-4 mr-1" />
                    Insert column above
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().addColumnAfter().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <InsertColumnIcon className="w-4 h-4 mr-1 rotate-180" />
                    Insert column below
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => executeCommand(duplicateColumn)}
                    className="!justify-start"
                  >
                    <CopyIcon className="w-4 h-4 mr-1" />
                    Duplicate column
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().deleteColumn().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Delete column
                  </Button>
                </ButtonGroup>
              </CardItemGroup>

              <Separator className="my-2" orientation="horizontal" />

              {/* Row Operations */}
              <CardItemGroup>
                <div className="table-menu-section-title !text-primary">
                  Rows
                </div>
                <ButtonGroup orientation="vertical" className="w-full">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().addRowBefore().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <InsertRowIcon className="w-4 h-4 mr-1" />
                    Insert row above
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().addRowAfter().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <InsertRowIcon className="w-4 h-4 mr-1 rotate-180" />
                    Insert row below
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => executeCommand(duplicateRow)}
                    className="!justify-start"
                  >
                    <CopyIcon className="w-4 h-4 mr-1" />
                    Duplicate row
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().deleteRow().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Delete row
                  </Button>
                </ButtonGroup>
              </CardItemGroup>

              <Separator className="my-2" orientation="horizontal" />

              {/* Cell Operations */}
              <CardItemGroup>
                <div className="table-menu-section-title !text-primary">
                  Cells
                </div>
                <ButtonGroup orientation="vertical" className="w-full">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().mergeCells().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <MergeCellIcon className="w-4 h-4 mr-1" />
                    Merge cells
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().splitCell().run(),
                      )
                    }
                    className="!justify-start"
                  >
                    <SplitCellIcon className="w-4 h-4 mr-1" />
                    Split cell
                  </Button>
                </ButtonGroup>
              </CardItemGroup>

              <Separator className="my-2" orientation="horizontal" />

              {/* Table Operations */}
              <CardItemGroup>
                <div className="table-menu-section-title !text-primary">
                  Table
                </div>
                <ButtonGroup orientation="vertical" className="w-full">
                  <Button
                    variant="ghost"
                    size="small"
                    className="!justify-start"
                    onClick={() =>
                      executeCommand(() =>
                        activeEditor.chain().focus().deleteTable().run(),
                      )
                    }
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Delete table
                  </Button>
                </ButtonGroup>
              </CardItemGroup>
            </CardBody>
          </Card>
        </div>
      )}
    </FloatingPortal>
  );
}
